import { PayloadAction } from '@reduxjs/toolkit';
import { IRootState } from './type';
import { TAssignOrder } from '../../types';

const reducers = {
  setAttributedOrders: (state: IRootState, action: PayloadAction<IRootState['attributedOrders']>) => ({
    ...state,
    attributedOrders: action.payload,
  }),
  setDailyStateSelectedLocation: (state: IRootState, action: PayloadAction<IRootState['dailyStateSelectedLocation']>) => ({
    ...state,
    dailyStateSelectedLocation: action.payload,
  }),
  setSelectedLocationId: (state: IRootState, action: PayloadAction<IRootState['selectedLocationId']>) => ({
    ...state,
    selectedLocationId: action.payload,
  }),
  setIsOrdersLoading: (state: IRootState, action: PayloadAction<IRootState['isOrdersLoading']>) => ({
    ...state,
    isOrdersLoading: action.payload,
  }),
  setOrdersMapping: (state: IRootState, action: PayloadAction<IRootState['ordersMapping']>) => ({
    ...state,
    ordersMapping: action.payload,
  }),
  setOrderCounts: (state: IRootState, action: PayloadAction<IRootState['orderCounts']>) => ({
    ...state,
    orderCounts: action.payload,
  }),
  appendAttributedOrders: (state: IRootState, action: PayloadAction<IRootState['attributedOrders']>) => ({
    ...state,
    attributedOrders: [...state.attributedOrders, ...action.payload],
  }),
  setOrdersChosenDates: (state: IRootState, action: PayloadAction<IRootState['ordersChosenDates']>) => ({
    ...state,
    ordersChosenDates: action.payload,
  }),
  setEmployeeRates: (state: IRootState, action: PayloadAction<IRootState['employeeRates']>) => ({
    ...state,
    employeeRates: action.payload,
  }),
  setChannelData: (state: IRootState, action: PayloadAction<IRootState['channelData']>) => ({
    ...state,
    channelData: action.payload,
  }),
  setLocationStats: (state: IRootState, action: PayloadAction<IRootState['locationStats']>) => ({
    ...state,
    locationStats: action.payload,
  }),
  processBestSalesman: (state: IRootState, action: PayloadAction<{ [key: string]: Array<TAssignOrder> }>) => {
    Object.entries(action.payload).forEach(([email, orders]) => {
      orders.forEach((order) => {
        const { locationId, summary } = order;

        if (!state.salesmanStats[locationId]) {
          // eslint-disable-next-line no-param-reassign
          state.salesmanStats[locationId] = [];
        }

        // Find the existing salesperson in the location array
        const existingSalesperson = state.salesmanStats[locationId].find((sales) => sales.userId === email);

        if (existingSalesperson) {
          // Update existing salesperson
          existingSalesperson.totalOrders += 1;
          existingSalesperson.totalSales += summary.netSales;
        } else {
          // Add new salesperson entry
          state.salesmanStats[locationId].push({
            userId: email,
            totalOrders: 1,
            totalSales: summary.netSales,
          });
        }

        // eslint-disable-next-line no-param-reassign
        state.salesmanStats[locationId] = state.salesmanStats[locationId]
          .sort((a, b) => b.totalSales - a.totalSales) // Sort descending by totalSales
          .slice(0, 5);
      });
    });
  },
  resetOrdersState: (state: IRootState) => ({
    ...state,
    attributedOrders: [],
    employeeRates: {},
    channelData: {},
    ordersTotals: {},
    locationStats: [],
    ordersMapping: [],
    salesmanStats: {},
    ordersByDates: [],
    ordersByLocations: {},
    promotersTotals: [],
    shopifyOrders: [],
    filteredOrders: [],
    dataByMemberAndLocation: {},
  }),
  setLocationMetrics: (state: IRootState, action: PayloadAction<IRootState['locationMetrics']>) => ({
    ...state,
    locationMetrics: action.payload,
  }),
  setOrdersByDates: (state: IRootState, action: PayloadAction<IRootState['ordersByDates']>) => ({
    ...state,
    ordersByDates: action.payload,
  }),
  appendOrdersByDates: (state: IRootState, action: PayloadAction<IRootState['ordersByDates']>) => ({
    ...state,
    ordersByDates: [...state.ordersByDates, ...action.payload],
  }),
  setDorChosenDate: (state: IRootState, action: PayloadAction<IRootState['dorChosenDate']>) => ({
    ...state,
    dorChosenDate: action.payload,
  }),
  setVariantExchangeResult: (state: IRootState, action: PayloadAction<IRootState['variantExchangeResult']>) => ({
    ...state,
    variantExchangeResult: action.payload,
  }),
  setSelectedOrder: (state: IRootState, action: PayloadAction<IRootState['selectedOrder']>) => ({
    ...state,
    selectedOrder: action.payload,
  }),
  setPromotersTotals: (state: IRootState, action: PayloadAction<IRootState['promotersTotals']>) => ({
    ...state,
    promotersTotals: action.payload,
  }),
  setMenuOptionName: (state: IRootState, action: PayloadAction<IRootState['menuOptionName']>) => ({
    ...state,
    menuOptionName: action.payload,
  }),
  setCameFromProductExchanges: (state: IRootState, action: PayloadAction<IRootState['cameFromProductExchanges']>) => ({
    ...state,
    cameFromProductExchanges: action.payload,
  }),
  mergeOrdersByLocation(state: IRootState, action: PayloadAction<IRootState['ordersByLocations']>) {
    const newOrdersByLocation = action.payload;

    Object.entries(newOrdersByLocation).forEach(([locationId, orders]) => {
      if (!state.ordersByLocations[locationId]) {
        // eslint-disable-next-line no-param-reassign
        state.ordersByLocations[locationId] = [];
      }

      // eslint-disable-next-line no-param-reassign
      state.ordersByLocations[locationId] = [
        ...state.ordersByLocations[locationId],
        ...orders,
      ];
    });
  },
  mergeOrderTotalsByLocation(state: IRootState, action: PayloadAction<IRootState['ordersTotals']>) {
    const newTotals = action.payload;

    Object.entries(newTotals).forEach(([locationId, totals]) => {
      if (!state.ordersTotals[String(locationId)]) {
        // eslint-disable-next-line no-param-reassign
        state.ordersTotals[String(locationId)] = {
          salesTotal: 0,
          totalOrders: 0,
          productsTotal: 0,
          commissionsTotal: 0,
        };
      }

      const existingTotals = state.ordersTotals[String(locationId)];

      existingTotals.salesTotal += totals.salesTotal;
      existingTotals.totalOrders += totals.totalOrders;
      existingTotals.productsTotal += totals.productsTotal;
      existingTotals.commissionsTotal += totals.commissionsTotal;
    });
  },
  processDataByMemberAndLocation: (state: IRootState, action: PayloadAction<{ [key: string]: Array<TAssignOrder> }>) => {
    Object.entries(action.payload).forEach(([memberKey, orders]) => {
      orders.forEach((order) => {
        if (memberKey) {
          if (!state.dataByMemberAndLocation[memberKey]) {
            // eslint-disable-next-line no-param-reassign
            state.dataByMemberAndLocation[memberKey] = {};
          }

          // eslint-disable-next-line no-param-reassign
          state.dataByMemberAndLocation[memberKey][order.locationId] = (state.dataByMemberAndLocation[memberKey][order.locationId] || 0)
              + (order.summary?.netSales || 0);
        }
      });
    });
  },
};

export {
  reducers,
};
